import { useResource } from 'datx-jsonapi-react';
import { GetServerSideProps, NextPage } from 'next';
import { NextSeo } from 'next-seo';

import { EventContentLecture } from '@/components/ui/features/events-app/event/EventContent/EventContentLecture';
import { EventAppLayout } from '@/components/ui/shared/layouts/EventAppLayout/EventAppLayout';
import { NoTicketCheck } from '@/components/ui/shared/overlays/NoTicketCheck/NoTicketCheck';
import { NoTicketCheckLectureAbstract } from '@/components/ui/shared/overlays/NoTicketCheck/NoTicketCheckAbstract';
import { EventContentSkeleton } from '@/containers/Event/EventContent.elements';
import { queryThinLectureWithTag } from '@/fetchers/thinLectures';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useSetReadNotification } from '@/hooks/useSetReadNotification';
import { queryThinEventSession } from '@/queries/thin-event-sessions';
import { queryThinEvent, queryThinEventConfigurationRelationship } from '@/queries/thin-events';
import { queryTranscriptsByLectureId } from '@/queries/transcripts';
import { ThinLecture } from '@/resources/ThinLecture';
import { generateStore } from '@/utils/generateStore';
import { splitIdAndSlug } from '@/utils/helperFunctions';
import { Hydrate, IHydrateProps } from '@/utils/swr/Hydrate';
import { StringParam, urlQueryStringValidate } from '@/utils/url-query-string';

export interface IUrlQueryStringParams {
  eventId?: string;
  sessionId?: string;
  lectureId?: string;
}

const LecturePage: NextPage<IHydrateProps> = ({ fallback }) => {
  const { eventId, sessionId, lectureId: lectureSlug } = useQueryParams<IUrlQueryStringParams>({
    eventId: StringParam,
    lectureId: StringParam,
    sessionId: StringParam,
  });

  const { id: lectureId } = splitIdAndSlug(lectureSlug);

  useSetReadNotification();

  const { data: lecture } = useResource([
    ThinLecture,
    lectureId,
    {
      include: ['tag', 'position_sorted_profiles'],
    },
  ]);

  const { data: event } = useResource(queryThinEvent(eventId));
  const { data: eventSession } = useResource(queryThinEventSession(sessionId));

  if (!event || !lecture || !eventSession) {
    return <EventContentSkeleton />;
  }

  return (
    <Hydrate fallback={fallback}>
      <NextSeo title={lecture.title} />
      <EventAppLayout>
        <NoTicketCheck eventId={eventId}>
          <NoTicketCheckLectureAbstract eventId={eventId}>
            <EventContentLecture lecture={lecture} event={event} eventSession={eventSession} />
          </NoTicketCheckLectureAbstract>
        </NoTicketCheck>
      </EventAppLayout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  const { eventId, sessionId, lectureId: lectureSlug } = urlQueryStringValidate<IUrlQueryStringParams>(context.query, {
    eventId: StringParam,
    sessionId: StringParam,
    lectureId: StringParam,
  });

  const { id: lectureId } = splitIdAndSlug(lectureSlug);

  try {
    const [eventConfigurationResponse] = await Promise.all([
      store.fetchResourceRelationship(queryThinEventConfigurationRelationship(eventId)),
      store.fetchResource(queryThinEvent(eventId)),
      store.fetchResources(queryTranscriptsByLectureId(lectureId)),
      store.fetchResource(queryThinEventSession(sessionId)),
      store.fetchResources(queryTranscriptsByLectureId(lectureId)),
      store.fetchResource(queryThinLectureWithTag(lectureId)),
    ]);

    if (!eventConfigurationResponse?.data?.sessionsPage) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        fallback: store.fallback,
      },
    };
  } catch (err) {
    return {
      props: {
        fallback: store.fallback,
      },
    };
  }
};

export default LecturePage;
